// Dialog.tsx
import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import Heading from './heading';
import Button from './button';
import { css } from '@emotion/react';
import Responsive from './responsive';

interface DialogProps {
  title?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  onClick?: () => void;
  presentation?: 'fullscreen' | 'dialog';
  size?: 'default' | 'reading';
}

const Outer = styled.dialog<{ presentation?: 'fullscreen' | 'dialog' }>(({ presentation = 'fullscreen' }) => ({
  border: 0,
  padding: 0,
  color: 'inherit',
  background: presentation === 'dialog' ? 'transparent' : 'light-dark(var(--light), var(--dark));',
  overflow: presentation === 'dialog' ? 'visible' : 'auto',
  zIndex: 1,
  ...(presentation === 'fullscreen' && {
    position: 'fixed',
    inset: 0,
    width: '100%',
    height: '100%',
    maxWidth: 'none',
    maxHeight: 'none',
    margin: 0,
  }),
  ...(presentation === 'dialog' && {
    width: 'calc(100% - 32px)',
    maxWidth: 'none',
    maxHeight: 'none',
    margin: 'auto',
    '&::backdrop': {
      background: 'rgba(0, 0, 0, 0.32)',
    },
    '@media (min-width: 640px)': {
      width: 'calc(100% - 64px)',
    },
  }),
}));

const Surface = styled.div<{ presentation?: 'fullscreen' | 'dialog'; size?: 'default' | 'reading' }>(({ presentation = 'fullscreen', size = 'default' }) => ({
  width: '100%',
  minHeight: presentation === 'dialog' ? 'auto' : '100%',
  background: 'light-dark(var(--light), var(--dark));',
  ...(presentation === 'dialog' && {
    display: 'grid',
    gridTemplateRows: 'auto minmax(0, 1fr) auto',
    margin: '0 auto',
    maxHeight: 'calc(100dvh - 32px - env(safe-area-inset-bottom))',
    overflow: 'hidden',
    border: '1px solid light-dark(var(--green-200), var(--green-800))',
    borderRadius: 16,
    boxShadow: '0 24px 80px rgba(0, 0, 0, 0.18), 0 8px 24px rgba(0, 0, 0, 0.12)',
    '@media (min-width: 640px)': {
      width: size === 'reading' ? 'min(720px, 100%)' : 'min(960px, 100%)',
      maxHeight: size === 'reading' ? 'calc(100dvh - 264px)' : 'calc(100dvh - 64px)',
    },
    ...(size !== 'reading' && { '@media (min-width: 1400px)': {
      width: 'min(1120px, 100%)',
    } }),
  }),
}));

const isBackdropClick = (event: React.MouseEvent<HTMLDialogElement>, surface: HTMLDivElement | null) => {
  const rect = surface?.getBoundingClientRect();

  if (!rect) {
    return false;
  }

  return (
    event.clientX < rect.left ||
    event.clientX > rect.right ||
    event.clientY < rect.top ||
    event.clientY > rect.bottom
  );
};

const Header = styled.div<{ presentation?: 'fullscreen' | 'dialog' }>(({ presentation = 'fullscreen' }) => ({
  padding: 24,
  position: presentation === 'dialog' ? 'sticky' : 'fixed',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  left: 0,
  top: 0,
  height: 74,
  background: 'light-dark(var(--light), var(--dark));',
  borderBottom: '1px solid light-dark(var(--green-200), var(--green-800))',
  zIndex: 1,
  '@media (min-width: 640px)': {
    height: 76,
  },
}));

const CloseButton = styled.button({
  position: 'absolute',
  right: 16,
  top: '50%',
  translate: '0 -50%',
  display: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  border: 0,
  borderRadius: 8,
  background: 'transparent',
  color: 'light-dark(var(--action-tertiary-text-light), var(--action-tertiary-text-dark))',
  cursor: 'pointer',
  '&:hover': {
    background: 'light-dark(var(--action-tertiary-hover-light), var(--action-tertiary-hover-dark))',
  },
  '&:focus-visible': {
    outline: '2px solid light-dark(var(--green-1000), var(--green-050))',
    outlineOffset: 2,
  },
  '@media (min-width: 500px)': {
    display: 'flex',
  },
});

const Child = styled.div<{ presentation?: 'fullscreen' | 'dialog' }>(({ presentation = 'fullscreen' }) => ({
  padding: 24,
  overflowY: presentation === 'dialog' ? 'auto' : undefined,
  paddingTop: presentation === 'dialog' ? 24 : 74 + 24,
  paddingBottom: presentation === 'dialog' ? 24 : 'calc(74px + 24px + env(safe-area-inset-bottom))',
  '@media (min-width: 500px)': {
    paddingBottom: presentation === 'dialog' ? 24 : 'calc(98px + 24px + env(safe-area-inset-bottom))',
  },
  '@media (min-width: 640px)': {
    padding: presentation === 'dialog' ? 32 : undefined,
    paddingTop: presentation === 'dialog' ? 32 : 76 + 48,
    paddingBottom: presentation === 'dialog' ? 32 : 'calc(102px + 24px + env(safe-area-inset-bottom))',
  },
}));

const Actions = styled.div<{ presentation?: 'fullscreen' | 'dialog' }>(({ presentation = 'fullscreen' }) => ({
  position: presentation === 'dialog' ? 'sticky' : 'fixed',
  width: '100%',
  left: 0,
  bottom: 0,
  background: 'light-dark(var(--light), var(--dark));',
  borderTop: '1px solid light-dark(var(--green-200), var(--green-800))',
  zIndex: 1,
  ...(presentation === 'dialog' && {
    '@media (min-width: 500px)': {
      display: 'none',
    },
  }),
}));

const ActionsInner = styled.div({
  display: 'flex',
  padding: 'calc(16px)',
  paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
  gap: 16,
  '@media (min-width: 500px)': {
    gap: 24,
    paddingTop: 24,
  },
});

const DialogActionsInner = styled(ActionsInner)({
  '@media (min-width: 500px)': {
    justifyContent: 'flex-end',
  },
});

const dialogHeading = css`
  color: light-dark(var(--dark), var(--light));
  text-align: center;
`;
  
const Dialog: React.FC<DialogProps> = ({title, children, onClick, actions, presentation, size}) => {
    const dialogRef = useRef<HTMLDialogElement>(null);
    const surfaceRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const dialog = dialogRef.current;

      if (dialog && !dialog.open) {
        dialog.showModal();
      }

      return () => {
        if (dialog?.open) {
          dialog.close();
        }
      };
    }, []);

    return (
      <Outer
        ref={dialogRef}
        presentation={presentation}
        onCancel={(event) => {
          event.preventDefault();
          onClick?.();
        }}
        onClick={(event) => {
          if (presentation === 'dialog' && isBackdropClick(event, surfaceRef.current)) {
            onClick?.();
          }
        }}
      >
        <Surface ref={surfaceRef} presentation={presentation} size={size}>
          <Header presentation={presentation}>
            {title && <Heading level="6" css={dialogHeading}>{title}</Heading>}
            {presentation === 'dialog' && (
              <CloseButton type="button" aria-label="Close" onClick={onClick}>
                <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                  <path
                    d="M5 5L15 15M15 5L5 15"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </CloseButton>
            )}
          </Header>
          <Child presentation={presentation}>{children}</Child>
          <Actions presentation={presentation}>
            <Responsive below={499}>
              <ActionsInner>
                <Button 
                  variant='primary' 
                  size="S" 
                  onClick={onClick} 
                  width='100%'>
                    Close
                </Button>
                {actions && actions}
              </ActionsInner>
            </Responsive>
            <Responsive above={500}>
              <DialogActionsInner>
                <Button 
                  variant='primary' 
                  size="M" 
                  onClick={onClick} 
                  width={presentation === 'dialog' ? 'auto' : '100%'}>
                    Close
                </Button>
                {actions && actions}
              </DialogActionsInner>
            </Responsive>
          </Actions>
        </Surface>
      </Outer>
    );
};

export default Dialog;
